import Extension from "../base/extension";

/**
 * Dialogs
 * =============================================================================
 * House behaviour for native <dialog>, and the declarative trigger API that
 * replaces Bootstrap's `data-bs-toggle="modal"`.
 *
 *   <button data-dialog-open="#my-dialog">Open</button>
 *   <dialog id="my-dialog" class="dialog" data-dialog-light-dismiss>
 *     <button data-dialog-close>Close</button>
 *   </dialog>
 *
 * Everything is delegated from document, so markup injected later (section
 * re-render, quick view, AJAX) works with no re-initialisation.
 *
 * WHAT THE PLATFORM ALREADY GIVES US (do not re-implement):
 * - Top layer stacking, so nested dialogs need no hoisting
 * - Focus containment, and the rest of the document going inert
 * - Escape to close, via a close request that fires `cancel` then `close`
 * - Page scroll locking, handled generally by extensions/scroll-lock.js, which
 *   observes the `open` attribute; a dialog needs no wiring at all for that
 *
 * WHAT STILL NEEDS US:
 * - **Backdrop-click dismiss** (`data-dialog-light-dismiss`). `closedby="any"`
 *   is NOT supported on iOS Safari at all (BCD, checked 2026-08-05; desktop
 *   Safari has it in preview only), so this shim is permanent infrastructure
 *   for the foreseeable future rather than a short bridge.
 * - **Focus return.** Restored to the opener on close for programmatic opens.
 * - **Non-dismissible dialogs** (`data-dialog-mandatory`, e.g. the age gate).
 *
 * @see docs/dialog-migration-plan.md
 */
class Dialogs extends Extension {
  /** Opener element per dialog, so focus can return there on close. */
  #openers = new WeakMap();

  /** Dialogs whose per-element listeners have been bound (bind once, not per open). */
  #wired = new WeakSet();

  /**
   * Whether the current pointer gesture began inside the dialog box, so a text
   * selection that starts on content and releases over the backdrop does not
   * dismiss the dialog.
   */
  #pointerStartedInside = false;

  init() {
    document.addEventListener("click", this.#onClick.bind(this));

    // Neither `close` nor `cancel` bubbles, so document-level delegation needs
    // the capture phase.
    document.addEventListener(
      "close",
      (event) => {
        if (event.target instanceof HTMLDialogElement) this.#onClosed(event.target);
      },
      true
    );

    // A mandatory dialog must refuse close requests however it was opened, so
    // this is delegated rather than bound at open time.
    document.addEventListener(
      "cancel",
      (event) => {
        const dialog = event.target;
        if (
          dialog instanceof HTMLDialogElement &&
          dialog.hasAttribute("data-dialog-mandatory")
        ) {
          // A close request is Escape on desktop AND the back button on mobile.
          event.preventDefault();
        }
      },
      true
    );
  }

  #onClick(event) {
    const opener = event.target.closest("[data-dialog-open]");
    if (opener) {
      const dialog = document.querySelector(opener.dataset.dialogOpen);
      if (!dialog) return;
      // Triggers should be real buttons, but stay safe if one is still an anchor.
      event.preventDefault();
      this.open(dialog, opener);
      return;
    }

    const closer = event.target.closest("[data-dialog-close]");
    if (closer) {
      const dialog = closer.closest("dialog");
      if (!dialog) return;
      event.preventDefault();
      dialog.close();
      return;
    }

    // Light dismiss: a backdrop click is targeted at the dialog element itself,
    // because ::backdrop is a pseudo-element and cannot be an event target.
    const dialog = event.target;
    if (
      dialog instanceof HTMLDialogElement &&
      dialog.hasAttribute("data-dialog-light-dismiss") &&
      !this.#pointerStartedInside &&
      !this.#isPointInside(dialog, event.clientX, event.clientY)
    ) {
      dialog.close();
    }
  }

  /**
   * A click whose target is the dialog can also land on its own padding, so
   * test the pointer against the box rather than trusting the target alone.
   */
  #isPointInside(dialog, x, y) {
    const box = dialog.getBoundingClientRect();
    return x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
  }

  open(dialog, opener = null) {
    if (!dialog || dialog.open) return;
    if (opener) this.#openers.set(dialog, opener);
    this.#wire(dialog);
    dialog.showModal();
    dialog.dispatchEvent(
      new CustomEvent("dialog:opened", { bubbles: true, detail: { dialog } })
    );
  }

  close(dialog) {
    if (dialog?.open) dialog.close();
  }

  /** Per-element listeners, bound once so repeat opens cannot stack them. */
  #wire(dialog) {
    if (this.#wired.has(dialog)) return;
    this.#wired.add(dialog);

    if (dialog.hasAttribute("data-dialog-light-dismiss")) {
      dialog.addEventListener("pointerdown", (event) => {
        this.#pointerStartedInside = this.#isPointInside(
          dialog,
          event.clientX,
          event.clientY
        );
      });
    }
  }

  #onClosed(dialog) {
    const opener = this.#openers.get(dialog);
    // preventScroll: the page is being unlocked around now (scroll-lock.js), so
    // letting focus scroll the page would fight the restored offset.
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    this.#openers.delete(dialog);
    this.#pointerStartedInside = false;

    dialog.dispatchEvent(
      new CustomEvent("dialog:closed", { bubbles: true, detail: { dialog } })
    );
  }
}

export default Dialogs;
